(function () {
  class StickySectionNav extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.sticky-section-nav__bar');
      this.placeholder = this.querySelector('.sticky-section-nav__placeholder');
      this.links = Array.from(this.querySelectorAll('.sticky-section-nav__link'));
      this.list = this.querySelector('.sticky-section-nav__list');
      this.lastActiveLink = null;
      this.targets = this._resolveTargets();
      this.isClickScrolling = false;
      this.clickScrollTimer = null;

      if (!this.bar || this.links.length === 0) return;

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onLinkClick = this._onLinkClick.bind(this);

      this.links.forEach((link) => link.addEventListener('click', this._onLinkClick));
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });

      this._onResize();
      this._onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this.links.forEach((link) => link.removeEventListener('click', this._onLinkClick));
      clearTimeout(this.clickScrollTimer);
    }

    _resolveTargets() {
      return this.links
        .map((link) => {
          const anchorId = link.dataset.anchor;
          if (!anchorId) return null;

          return (
            document.getElementById(anchorId) ||
            document.querySelector(`[data-section-anchor="${anchorId}"]`) ||
            document.getElementById(`shopify-section-${anchorId}`)
          );
        })
        .filter(Boolean);
    }

    _readPxVar(name) {
      const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
      return Number.isFinite(value) ? value : 0;
    }

    _getHeaderBottom() {
      const headerVisible = this._readPxVar('--header-is-visible');
      if (headerVisible === 0) return 0;

      const headerGroup = document.querySelector('.shopify-section-group-header-group');
      if (headerGroup) {
        let bottom = 0;

        headerGroup.querySelectorAll('.shopify-section').forEach((section) => {
          const rect = section.getBoundingClientRect();
          if (rect.height > 0) {
            bottom = Math.max(bottom, rect.bottom);
          }
        });

        if (bottom > 0) return bottom;
      }

      const headerObserver = document.querySelector('height-observer[variable="header"]');
      if (headerObserver) {
        return headerObserver.getBoundingClientRect().bottom;
      }

      return 0;
    }

    _getStickyOffset() {
      const measuredBottom = this._getHeaderBottom();
      if (measuredBottom > 0) return measuredBottom;

      const headerVisible = this._readPxVar('--header-is-visible') || 1;
      const announcementHeight =
        this._readPxVar('--sticky-announcement-bar-enabled') * this._readPxVar('--announcement-bar-height');
      const headerHeight = this._readPxVar('--sticky-header-enabled') * this._readPxVar('--header-height');

      return headerVisible * (announcementHeight + headerHeight);
    }

    _getNavHeight() {
      return this.bar ? this.bar.offsetHeight : 0;
    }

    _getScrollOffset() {
      return this._getStickyOffset() + this._getNavHeight();
    }

    _onResize() {
      if (this.bar.classList.contains('is-fixed')) {
        this.placeholder.style.height = `${this._getNavHeight()}px`;
      }
    }

    _onScroll() {
      this._updateFixedState();
      if (this.isClickScrolling) return this._scheduleClickScrollEnd();
      this._updateActiveLink();
    }

    _scheduleClickScrollEnd() {
      clearTimeout(this.clickScrollTimer);
      this.clickScrollTimer = setTimeout(() => {
        this.isClickScrolling = false;
        this._updateActiveLink();
      }, 150);
    }

    _updateFixedState() {
      const stickyOffset = this._getStickyOffset();
      const sectionTop = this.getBoundingClientRect().top + window.scrollY;
      const shouldFix = window.scrollY + stickyOffset >= sectionTop;

      if (shouldFix) {
        if (!this.bar.classList.contains('is-fixed')) {
          this.placeholder.style.height = `${this._getNavHeight()}px`;
          this.placeholder.style.display = 'block';
          this.bar.classList.add('is-fixed');
        }

        this.bar.style.top = `${this._getStickyOffset()}px`;
      } else {
        this.bar.classList.remove('is-fixed');
        this.bar.style.removeProperty('top');
        this.placeholder.style.display = 'none';
        this.placeholder.style.height = '0';
      }
    }

    _updateActiveLink() {
      const offset = this._getScrollOffset();
      const activationTolerance = 2;
      const activationLine = offset + activationTolerance;
      let activeIndex = -1;
      let closestTop = -Infinity;
      let firstTargetIndex = 0;
      let firstTargetTop = Infinity;

      this.links.forEach((link, index) => {
        const anchorId = link.dataset.anchor;
        const target =
          document.getElementById(anchorId) ||
          document.querySelector(`[data-section-anchor="${anchorId}"]`) ||
          document.getElementById(`shopify-section-${anchorId}`);

        if (!target) return;

        const targetTop = target.getBoundingClientRect().top;
        if (targetTop < firstTargetTop) {
          firstTargetTop = targetTop;
          firstTargetIndex = index;
        }
        if (targetTop <= activationLine && targetTop > closestTop) {
          closestTop = targetTop;
          activeIndex = index;
        }
      });

      if (activeIndex === -1) activeIndex = firstTargetIndex;

      this.links.forEach((link, index) => {
        const isActive = index === activeIndex;
        link.classList.toggle('is-active', isActive);
        link.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      const activeLink = this.links[activeIndex];
      if (activeLink !== this.lastActiveLink) {
        this.lastActiveLink = activeLink;
        this._ensureActiveLinkVisible(activeLink);
      }
    }

    _ensureActiveLinkVisible(link) {
      if (!link || !this.list || this.dataset.autoScrollActive !== 'true') return;

      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      const index = this.links.indexOf(link);
      if (index === 0) {
        this.list.scrollTo({ left: 0, behavior });
        return;
      }
      if (index === this.links.length - 1) {
        this.list.scrollTo({ left: this.list.scrollWidth, behavior });
        return;
      }

      const listRect = this.list.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const targetLeft = this.list.scrollLeft + linkRect.left - listRect.left - (this.list.clientWidth - linkRect.width) / 2;
      this.list.scrollTo({ left: targetLeft, behavior });
    }

    _onLinkClick(event) {
      event.preventDefault();

      const link = event.currentTarget;
      const anchorId = link.dataset.anchor;
      const target =
        document.getElementById(anchorId) ||
        document.querySelector(`[data-section-anchor="${anchorId}"]`) ||
        document.getElementById(`shopify-section-${anchorId}`);

      if (!target) return;

      this.links.forEach((item) => item.classList.remove('is-active'));
      link.classList.add('is-active');
      this.lastActiveLink = link;
      this._ensureActiveLinkVisible(link);

      this.isClickScrolling = true;

      const targetTop = target.getBoundingClientRect().top + window.scrollY - this._getScrollOffset();
      window.scrollTo({ top: targetTop, behavior: 'smooth' });
      this._scheduleClickScrollEnd();
    }
  }

  if (!window.customElements.get('sticky-section-nav')) {
    window.customElements.define('sticky-section-nav', StickySectionNav);
  }
})();
